import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, debounceTime } from 'rxjs';

export type UserConfigKey =
  | 'theme'
  | 'color_theme'
  | 'language'
  | 'timezone'
  | 'map_basemap'
  | 'map_basemap_auto'
  | 'geo_score_filter'
  | 'geo_layer';

export const USER_CONFIG_DEFAULTS: Record<UserConfigKey, string> = {
  theme:            'system',
  color_theme:      'azure',
  language:         'it',
  timezone:         'Europe/Rome',
  map_basemap:      'osm',
  map_basemap_auto: 'true',
  geo_score_filter: 'high',
  geo_layer:        'cluster',
};

/** IANA timezone list for the preferences UI */
export const TIMEZONES = [
  'Europe/Rome', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Europe/Madrid', 'Europe/Athens', 'Europe/Warsaw', 'Europe/Amsterdam',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Sao_Paulo', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Tokyo',
  'Asia/Shanghai', 'Australia/Sydney', 'Pacific/Auckland', 'UTC',
];

@Injectable({ providedIn: 'root' })
export class UserConfigService {
  private http = inject(HttpClient);

  /** Full config map as a signal — single source of truth */
  private _config = signal<Record<string, string>>({ ...USER_CONFIG_DEFAULTS });

  /** Pending writes buffer — flushed via debounce */
  private _pending: Record<string, string> = {};
  private _save$ = new Subject<void>();

  constructor() {
    // Debounce saves: wait 600ms after last set() before hitting the API
    this._save$.pipe(debounceTime(600)).subscribe(() => this._flush());
  }

  /** Load config from backend. Call once at app init after authentication. */
  async init(): Promise<void> {
    try {
      const cfg = await firstValueFrom(
        this.http.get<Record<string, string>>('/api/v1/me/config')
      );
      this._config.set({ ...USER_CONFIG_DEFAULTS, ...cfg });
    } catch {
      // Not authenticated yet or network error — use defaults
    }
  }

  /** Get a config value (falls back to default if not set). */
  get(key: UserConfigKey): string {
    return this._config()[key] ?? USER_CONFIG_DEFAULTS[key];
  }

  /** Reactive: returns a computed signal for a single key. */
  select(key: UserConfigKey) {
    return computed(() => this._config()[key] ?? USER_CONFIG_DEFAULTS[key]);
  }

  /** Set one or more config values. Updates signal immediately, saves to backend debounced. */
  set(updates: Partial<Record<UserConfigKey, string>>): void {
    this._config.update(c => ({ ...c, ...updates }));
    Object.assign(this._pending, updates);
    this._save$.next();
  }

  /** Force-save immediately (e.g. on page unload). */
  flush(): void {
    if (Object.keys(this._pending).length) this._flush();
  }

  private _flush(): void {
    const payload = { ...this._pending };
    this._pending = {};
    if (!Object.keys(payload).length) return;
    this.http.put<Record<string, string>>('/api/v1/me/config', payload)
      .subscribe({ next: cfg => this._config.set({ ...USER_CONFIG_DEFAULTS, ...cfg }) });
  }
}
